import { useCallback, useRef } from 'react';
import { authApi, ApiError, AuthUser, setSessionToken } from '../../services/api';
import { logger } from '../../utils/logger';

export const TOKEN_REFRESH_THRESHOLD = 0.8;

export function useAuthTokenLifecycle() {
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const tokenExpiresAtRef = useRef<number | null>(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const scheduleTokenRefresh = useCallback(
    (
      expiresIn: number,
      onTokenRefreshed: (accessToken: string, expiresIn: number, expiresAt: number) => void,
      onRefreshFailed: () => void
    ) => {
      clearRefreshTimer();
      const refreshInMs = expiresIn * TOKEN_REFRESH_THRESHOLD * 1000;

      refreshTimerRef.current = setTimeout(async () => {
        try {
          const response = await authApi.refreshToken();
          tokenExpiresAtRef.current = response.expiresAt;
          onTokenRefreshed(response.accessToken, response.expiresIn, response.expiresAt);
        } catch (err) {
          logger.error('[Auth] Token refresh failed:', err);
          onRefreshFailed();
        }
      }, refreshInMs);
    },
    [clearRefreshTimer]
  );

  const applyValidatedSession = useCallback(
    (accessToken: string, expiresIn: number, user: AuthUser, sessionToken?: string) => {
      tokenExpiresAtRef.current = Date.now() + expiresIn * 1000;
      if (sessionToken) {
        setSessionToken(sessionToken);
      }
      return { user, accessToken, expiresIn };
    },
    []
  );

  const clearSession = useCallback(() => {
    clearRefreshTimer();
    setSessionToken(null);
    tokenExpiresAtRef.current = null;
  }, [clearRefreshTimer]);

  return {
    tokenExpiresAtRef,
    clearRefreshTimer,
    scheduleTokenRefresh,
    applyValidatedSession,
    clearSession,
  };
}

export function mapAuthError(err: unknown): string {
  if (err instanceof Error && !(err instanceof ApiError)) {
    return err.message;
  }

  if (err instanceof ApiError) {
    switch (err.code) {
      case 'INVALID_SAP_SIGNATURE':
        return 'SAP authentication verification failed.';
      case 'INVALID_NICE_CREDENTIALS':
        return 'NICE sign-in failed or expired. Try again.';
      case 'TENANT_NOT_CONFIGURED':
        return 'This SAP system is not configured for the connector.';
      default:
        return err.message;
    }
  }

  return 'Authentication failed. Please try again.';
}
